/* ============================================================================
   MapleWeld — homepage v3 "Storefront"

   One delegated listener set for the whole homepage. Loaded with `defer` so it
   is never in the LCP critical path.

   Responsibilities:
     1. Quantity stepper on best-seller cards
     2. Card-level and hero-deal add-to-cart via /cart/add.js
     3. GA4 ecommerce events: view_item_list, select_item, add_to_cart,
        select_promotion, and the custom select_service_tile
     4. Cart-count refresh after an add

   GA4 is pushed to window.dataLayer AND, if gtag() exists, called directly.
   Shopify stores commonly have GA4 via the native channel (gtag, no dataLayer)
   OR via GTM (dataLayer, no gtag). Doing both costs nothing and removes a whole
   class of "events aren't firing" tickets. Duplicate-safe: only one transport
   is ever live.
   ============================================================================ */
(function () {
  'use strict';

  /* Gate: bind nothing unless sections/mw-home-meta.liquid (role=open) rendered
     its marker. Delegation is bound to `document` rather than a wrapper element
     — Shopify wraps each section in its own #shopify-section-* node, so no
     single element can contain them all. */
  if (!document.getElementById('mw-home-marker')) return;

  var CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var hasGtag = typeof window.gtag === 'function';
  var hasDataLayer = Array.isArray(window.dataLayer);
  window.dataLayer = window.dataLayer || [];

  function track(eventName, params) {
    try {
      window.dataLayer.push(Object.assign({ event: eventName }, params));
      if (hasGtag && !hasDataLayer) window.gtag('event', eventName, params);
    } catch (e) { /* analytics must never break the page */ }
  }

  function cardToItem(card, qty, index) {
    return {
      item_id: card.dataset.mwSku || card.dataset.mwProductId || '',
      item_name: card.dataset.mwTitle || '',
      item_brand: card.dataset.mwVendor || '',
      item_category: card.dataset.mwType || '',
      item_variant: card.dataset.mwVariantTitle || '',
      item_list_name: card.dataset.mwListName || '',
      item_list_id: card.dataset.mwListId || '',
      price: parseFloat(card.dataset.mwPrice || '0'),
      index: typeof index === 'number' ? index : parseInt(card.dataset.mwIndex || '0', 10),
      quantity: qty || 1
    };
  }

  /* ---------- 1. view_item_list ------------------------------------------
     One batched event per grid, fired once, at 50% visibility. Batching keeps
     GA4 hit volume sane on a page with four product grids. */

  var lists = document.querySelectorAll('[data-mw-list]');
  if (lists.length && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var list = entry.target;
        obs.unobserve(list);
        var cards = list.querySelectorAll('[data-mw-card]');
        if (!cards.length) return;
        track('view_item_list', {
          item_list_id: list.dataset.mwListId || '',
          item_list_name: list.dataset.mwListName || '',
          items: Array.prototype.map.call(cards, function (c) { return cardToItem(c, 1); })
        });
      });
    }, { threshold: 0.5 });
    Array.prototype.forEach.call(lists, function (l) { io.observe(l); });
  }

  /* ---------- 2. Quantity stepper ---------------------------------------- */

  document.addEventListener('click', function (evt) {
    var step = evt.target.closest('[data-mw-qty-step]');
    if (!step) return;
    var input = step.parentElement.querySelector('[data-mw-qty]');
    if (!input) return;
    var next = (parseInt(input.value, 10) || 1) + parseInt(step.dataset.mwQtyStep, 10);
    input.value = Math.min(99, Math.max(1, next));
  });

  /* ---------- 3. select_promotion / service tiles / select_item -----------
     Hero CTAs, category rail, offer tiles and application tiles all report
     through select_promotion; creative_slot separates them in reporting, so
     the GA4 property needs no new event schema. */

  document.addEventListener('click', function (evt) {
    var promo = evt.target.closest('[data-mw-promo]');
    if (promo) {
      track('select_promotion', {
        promotion_id: promo.dataset.mwPromoId || '',
        promotion_name: promo.dataset.mwPromo || '',
        creative_slot: promo.dataset.mwPromoSlot || ''
      });
    }

    var tile = evt.target.closest('[data-mw-service-tile]');
    if (tile) {
      track('select_service_tile', {
        tile_name: tile.dataset.mwServiceTile || '',
        tile_destination: tile.getAttribute('href') || ''
      });
    }

    var link = evt.target.closest('[data-mw-card] a');
    if (link && !evt.target.closest('[data-mw-atc]')) {
      var card = link.closest('[data-mw-card]');
      track('select_item', {
        item_list_id: card.dataset.mwListId || '',
        item_list_name: card.dataset.mwListName || '',
        items: [cardToItem(card, 1)]
      });
    }
  }, { passive: true });

  /* ---------- 4. Add to cart --------------------------------------------- */

  function refreshCartCount() {
    return fetch(ROOT + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Molla renders its counter in several header variants; update every
           known hook rather than guessing which header is active. */
        var sel = '.cart-count, .count-cart, [data-cart-count], .kt-cart-count, .cart-dropdown .count';
        document.querySelectorAll(sel).forEach(function (el) { el.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('mw:cart:updated', { detail: cart }));
        return cart;
      })
      .catch(function () { /* non-fatal */ });
  }

  document.addEventListener('submit', function (evt) {
    var form = evt.target.closest('[data-mw-atc-form]');
    if (!form) return;
    evt.preventDefault();

    var btn = form.querySelector('[data-mw-atc]');
    var card = form.closest('[data-mw-card]');
    var idEl = form.querySelector('[name="id"]');
    var qtyEl = form.querySelector('[data-mw-qty]');
    var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
    var variantId = idEl && idEl.value;

    if (!variantId || !btn || btn.disabled) return;

    var original = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = btn.dataset.mwAddingLabel || 'Adding…';

    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
    })
      .then(function (res) { return res.json().then(function (b) { return { ok: res.ok, body: b }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error((r.body && r.body.description) || 'Add to cart failed');
        if (card) {
          track('add_to_cart', {
            currency: CURRENCY,
            value: parseFloat(card.dataset.mwPrice || '0') * qty,
            items: [cardToItem(card, qty)]
          });
        }
        btn.classList.add('is-added');
        btn.textContent = btn.dataset.mwAddedLabel || 'Added ✓';
        return refreshCartCount();
      })
      .catch(function (err) {
        btn.textContent = (err && err.message) ? String(err.message).slice(0, 40) : 'Unavailable';
      })
      .finally(function () {
        setTimeout(function () {
          btn.disabled = false;
          btn.classList.remove('is-added');
          btn.innerHTML = original;
        }, 2200);
      });
  });

  /* ---------- 5. Body class for the mobile action bar --------------------- */
  document.body.classList.add('mw-home-body');
})();
